/* LEGAL-NOTICE
   © Copyright 2018 - Quid Informatica S.p.A. - All Rights Reserved
   https://quidinfo.it/qinetic/licence.html - "Quid Consumer Lending Platform"
   Deposito SIAE n. 012702 del 26 Luglio 2018
LEGAL-NOTICE */
import {
  CustomerDTO,
  PositionDTO,
  QuotationDTO,
  ObligationDTO,
  AddressDTO
} from '@api/models/';
import { DtoManager } from '../generic';
import { AddressType } from '../address';

export class Position extends DtoManager<PositionDTO> {
  constructor(dto: PositionDTO) {
    super(dto);
  }

  checkIntegrity(): void {
    if (!this.data || !this.data.positionId) {
      throw new Error(`Position data is malformed`);
    }
  }

  getQuotation(): QuotationDTO {
    return (this.data && this.data.product) || undefined;
  }

  getObligations(): ObligationDTO[] {
    return (this.data && this.data.obbligationDtoList) || [];
  }

  getCustomer(): CustomerDTO {
    return (this.data && this.data.customer) || undefined;
  }

  getCustomerAddress(type: AddressType): AddressDTO {
    const customer = this.getCustomer();

    if (!customer) {
      return;
    }

    if (type === 'domicile') {
      return customer.domicileAddress && { ...customer.domicileAddress };
    } else if (type === 'residence') {
      return customer.residenceAddress && { ...customer.residenceAddress };
    } else if (type === 'shipping') {
      return customer.shippingAddress && { ...customer.shippingAddress };
    }
  }

  getCustomerNdgType(): string {
    const customer = this.getCustomer();
    return (customer && customer.ndgType) || null;
  }

  getCustomerName(): string {
    const customer = this.getCustomer();
    return (customer && customer.firstName) || null;
  }

  getStatus(): string {
    return this.data && this.data.statusCode;
  }

  getPhase(): string {
    return this.data && this.data.phaseCode;
  }

  getCampaignCode(): string {
    return this.data && this.data.preEvaluationType;
  }

  // pensionato altri enti
  isPensioner(): boolean {
    return this.hasNdgType('P');
  }

  // pensionato inps
  isPensionerINPS(): boolean {
    return this.hasNdgType('I');
  }

  // dipendente
  isEmployee(): boolean {
    return this.hasNdgType('D');
  }

  // dipendente mef
  isEmployeeMEF(): boolean {
    return this.hasNdgType('M');
  }

  // cliente predeliberato
  isPredeliberate(): boolean {
    return !!this.getCampaignCode();
  }

  // pratica cqs richiesta fido
  isCqsRf(): boolean {
    return this.hasNdgType('RF');
  }

  // pratica cqs utilizzo fido
  isCqsUf(): boolean {
    return this.hasNdgType('UF');
  }

  // pratica ritirata
  isRetreat(): boolean {
    return this.hasPhase('900');
  }

  // pratica liquidata
  isLiq(): boolean {
    return this.hasPhase('400');
  }

  // pratica aperta
  isOpen(): boolean {
    return !this.isRetreat() && !this.isLiq();
  }

  hasNdgType(ndgType: string): boolean {
    return this.getCustomerNdgType() === ndgType;
  }

  hasPhaseAndStatus(combination: string): boolean {
    const parts = combination.split('|');
    const reqPhase = parts[0].trim();
    const reqStatus = parts[1].trim();

    return this.hasPhase(reqPhase) && this.hasStatus(reqStatus);
  }

  hasPhase(phase: string): boolean {
    return this.getPhase() === phase;
  }

  hasStatus(status: string): boolean {
    return this.getStatus() === status;
  }
}
